import React, { useEffect, useRef } from "react";

// A clean, sleek "neural data field": drifting nodes that link when near,
// with a soft mouse-reactive pull. Grounded, not goofy.
// `light` renders dark monochrome nodes for a light background (crisp B/W).
export function NeuralField({ className = "", light = false, tone = "brand" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const palette = light
      ? { a: [17, 17, 19], b: [82, 82, 91], line: "17,17,19", mouse: "0,0,0", lineMax: 0.2, mouseMax: 0.3, shadow: "rgba(0,0,0,0.22)", shadowBlur: 3 }
      : tone === "neon"
      ? { a: [34, 211, 238], b: [139, 92, 246], line: "56,189,248", mouse: "255,255,255", lineMax: 0.3, mouseMax: 0.34, shadow: "rgba(56,189,248,0.5)", shadowBlur: 6 }
      : { a: [201, 162, 39], b: [96, 148, 108], line: "201,162,39", mouse: "255,255,255", lineMax: 0.22, mouseMax: 0.28, shadow: null, shadowBlur: 4 };

    const nodes = [];
    let w = 0, h = 0, dpr = 1;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = Math.max(1, w * dpr); canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const seed = () => {
      nodes.length = 0;
      const count = Math.round(Math.min(70, Math.max(36, (w * h) / 20000)));
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * w, y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.22, vy: (Math.random() - 0.5) * 0.22,
          r: Math.random() * 1.5 + 0.7,
          c: Math.random() > 0.62 ? palette.a : palette.b,
        });
      }
    };

    resize(); seed();

    const LINK = 148;
    const MOUSE_LINK = 190;
    // Desktop (hover-capable, fine pointer) gets the MOVING lines. Phones get a
    // single static frame. iPad (coarse >=640) is disabled above (returns null).
    const animated = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const mouse = { x: -9999, y: -9999 };
    let raf = 0, t = 0, lastFrame = 0;
    const FRAME_MS = 1000 / 30;

    const drawFrame = (moving) => {
      if (moving) t += 0.006;
      ctx.clearRect(0, 0, w, h);
      if (moving) {
        for (const n of nodes) {
          n.x += n.vx; n.y += n.vy;
          if (n.x < -20) n.x = w + 20; else if (n.x > w + 20) n.x = -20;
          if (n.y < -20) n.y = h + 20; else if (n.y > h + 20) n.y = -20;
          const mdx = mouse.x - n.x, mdy = mouse.y - n.y;
          const md = Math.hypot(mdx, mdy);
          if (md < MOUSE_LINK && md > 0.01) { const f = (1 - md / MOUSE_LINK) * 0.02; n.x += mdx * f; n.y += mdy * f; }
        }
      }
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK) {
            ctx.strokeStyle = `rgba(${palette.line},${(1 - d / LINK) * palette.lineMax})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
        if (moving) {
          const mdx = a.x - mouse.x, mdy = a.y - mouse.y;
          const md = Math.hypot(mdx, mdy);
          if (md < MOUSE_LINK) {
            ctx.strokeStyle = `rgba(${palette.mouse},${(1 - md / MOUSE_LINK) * palette.mouseMax})`;
            ctx.lineWidth = 0.7;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(mouse.x, mouse.y); ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        const pulse = moving ? (0.62 + Math.sin(t * 2.4 + n.x * 0.012 + n.y * 0.01) * 0.22) : 1;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${n.c[0]},${n.c[1]},${n.c[2]},${0.7 * pulse})`;
        ctx.shadowBlur = palette.shadowBlur;
        ctx.shadowColor = palette.shadow || `rgba(${n.c[0]},${n.c[1]},${n.c[2]},0.55)`;
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    };

    const loop = (now = 0) => {
      if (now - lastFrame >= FRAME_MS) { lastFrame = now; drawFrame(true); }
      raf = requestAnimationFrame(loop);
    };

    if (animated) raf = requestAnimationFrame(loop);
    else drawFrame(false);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (e.clientX == null) return;
      mouse.x = e.clientX - rect.left; mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { resize(); seed(); if (!animated) drawFrame(false); }, 150);
    };
    if (animated) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerout", onLeave);
    }
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      if (animated) {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerout", onLeave);
      }
      window.removeEventListener("resize", onResize);
    };
  }, [light, tone]);

  return <canvas ref={ref} className={className} data-testid="neural-field" />;
}
